import { useEffect, useState } from "react";
import { QrCode, Eye, Star, Inbox as InboxIcon, TrendingUp, MousePointerClick, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5 fade-up" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[#a0a5b5] text-sm">{label}</span>
      <Icon className={`w-5 h-5 ${accent || "text-[#64697a]"}`} />
    </div>
    <div className="font-display text-3xl font-extrabold tracking-tight">{value}</div>
  </div>
);

const chartTooltip = {
  contentStyle: { background: "#161920", border: "1px solid #262a33", borderRadius: 12, color: "#fff" },
  labelStyle: { color: "#a0a5b5" },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const doReset = async () => {
    setResetting(true);
    try {
      await api.post("/admin/reset-stats");
      const r = await api.get("/admin/stats");
      setStats(r.data);
      setConfirmOpen(false);
      toast.success("Statistiques remises à zéro. Le suivi officiel démarre maintenant.");
    } catch (e) {
      toast.error("Erreur lors de la remise à zéro.");
    } finally {
      setResetting(false);
    }
  };

  if (!stats)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00e676] animate-spin" /></div>;

  const trackingDate = stats.tracking_started_at
    ? new Date(stats.tracking_started_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Tableau de bord</h1>
          <p className="text-[#64697a] mt-1">Vue d'ensemble de votre activité avis clients</p>
          {trackingDate && (
            <p className="text-xs text-[#64697a] mt-1" data-testid="tracking-since">Suivi officiel depuis le {trackingDate}</p>
          )}
        </div>
        <button
          onClick={() => setConfirmOpen(true)}
          data-testid="reset-stats-btn"
          className="flex items-center gap-2 bg-[#2a2d35] hover:bg-[#363a45] text-white text-sm font-medium rounded-xl px-4 py-2 transition-colors"
        >
          <RotateCcw className="w-4 h-4" /> Réinitialiser les statistiques
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setConfirmOpen(false)}>
          <div className="bg-[#161920] border border-[#262a33] rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="reset-confirm-modal">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-6 h-6 text-[#F59E0B]" />
              <h2 className="font-display text-xl font-bold">Réinitialiser les statistiques ?</h2>
            </div>
            <p className="text-[#a0a5b5] text-sm mb-5">
              Cette action supprime définitivement tous les scans, visites, clics et retours enregistrés jusqu'à présent.
              Le suivi officiel repartira de zéro à partir de maintenant. Vos QR codes, liens et paramètres ne sont pas affectés.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-[#a0a5b5] hover:text-white transition-colors" data-testid="reset-cancel-btn">
                Annuler
              </button>
              <button onClick={doReset} disabled={resetting} data-testid="reset-confirm-btn"
                className="px-5 py-2 rounded-full text-sm font-bold bg-[#EF4444] hover:bg-[#dc2626] text-white transition-colors flex items-center gap-2 disabled:opacity-60">
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Tout remettre à zéro
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={QrCode} label="Scans totaux" value={stats.total_scans} accent="text-[#00e676]" testid="stat-total-scans" />
        <Stat icon={QrCode} label="Scans aujourd'hui" value={stats.scans_today} testid="stat-scans-today" />
        <Stat icon={QrCode} label="Scans cette semaine" value={stats.scans_week} testid="stat-scans-week" />
        <Stat icon={QrCode} label="Scans ce mois" value={stats.scans_month} testid="stat-scans-month" />
        <Stat icon={Eye} label="Visites page" value={stats.total_views} testid="stat-views" />
        <Stat icon={Star} label="Clics PagesJaunes" value={stats.total_pj_clicks} accent="text-[#00e676]" testid="stat-pj-clicks" />
        <Stat icon={Star} label="Clics Google" value={stats.total_google_clicks ?? 0} accent="text-[#4285F4]" testid="stat-google-clicks" />
        <Stat icon={InboxIcon} label="Retours privés" value={stats.total_forms} testid="stat-forms" />
        <Stat icon={MousePointerClick} label="Taux clic PagesJaunes" value={`${stats.click_rate}%`} testid="stat-click-rate" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5 flex items-center gap-4">
          <TrendingUp className="w-8 h-8 text-[#00e676]" />
          <div>
            <div className="text-[#a0a5b5] text-sm">Taux de conversion scans → clics</div>
            <div className="font-display text-2xl font-extrabold" data-testid="stat-conversion">{stats.conversion_rate}%</div>
          </div>
        </div>
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5 flex items-center gap-4">
          <InboxIcon className="w-8 h-8 text-[#3B82F6]" />
          <div>
            <div className="text-[#a0a5b5] text-sm">Nouveaux retours à traiter</div>
            <div className="font-display text-2xl font-extrabold" data-testid="stat-new-forms">{stats.new_forms}</div>
          </div>
        </div>
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5 flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-[#EF4444]" />
          <div>
            <div className="text-[#a0a5b5] text-sm">Retours négatifs filtrés</div>
            <div className="font-display text-2xl font-extrabold" data-testid="stat-negative-forms">{stats.negative_forms ?? 0}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5">
          <h3 className="font-semibold mb-4">Évolution hebdomadaire (8 semaines)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.weekly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262a33" />
              <XAxis dataKey="label" stroke="#64697a" fontSize={12} />
              <YAxis stroke="#64697a" fontSize={12} allowDecimals={false} />
              <Tooltip {...chartTooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="scans" name="Scans" fill="#00e676" radius={[4, 4, 0, 0]} />
              <Bar dataKey="clicks" name="Clics PJ" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="forms" name="Retours" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#161920] border border-[#262a33] rounded-xl p-5">
          <h3 className="font-semibold mb-4">Évolution mensuelle (6 mois)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={stats.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262a33" />
              <XAxis dataKey="label" stroke="#64697a" fontSize={12} />
              <YAxis stroke="#64697a" fontSize={12} allowDecimals={false} />
              <Tooltip {...chartTooltip} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="scans" name="Scans" stroke="#00e676" strokeWidth={2} />
              <Line type="monotone" dataKey="clicks" name="Clics PJ" stroke="#3B82F6" strokeWidth={2} />
              <Line type="monotone" dataKey="forms" name="Retours" stroke="#8B5CF6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
